import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import {
  ClipboardCheck,
  Plus,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Package,
  MapPin,
  User,
  Eye,
} from 'lucide-react';

interface Audit {
  id: string;
  toolroom_id: string;
  toolroom_name: string;
  started_by: string;
  started_by_name: string;
  started_at: string;
  completed_at?: string;
  status: 'in_progress' | 'completed' | 'cancelled';
  notes?: string;
  scan_count: number;
  discrepancy_count: number;
}

interface AuditScan {
  id: string;
  audit_id: string;
  location_id: string;
  location_name: string;
  location_barcode?: string;
  scanned_by: string;
  scanned_by_name: string;
  expected_quantity: number;
  actual_quantity: number;
  discrepancy: number;
  scanned_at: string;
  notes?: string;
}

interface AuditDetails extends Audit {
  scans: AuditScan[];
  summary: {
    total_scans: number;
    over_count: number;
    under_count: number;
    match_count: number;
  };
}

interface Toolroom {
  id: string;
  name: string;
}

interface PendingLocation {
  id: string;
  name: string;
  barcode?: string;
  location_type: string;
  expected_quantity: number;
}

export function Audit() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'in_progress' | 'completed' | 'cancelled' | 'all'>('all');
  const [showStartModal, setShowStartModal] = useState(false);
  const [viewingAudit, setViewingAudit] = useState<string | null>(null);
  const [scanningAudit, setScanningAudit] = useState<string | null>(null);

  const { data: audits, isLoading } = useQuery({
    queryKey: ['audits', statusFilter],
    queryFn: async () => {
      const res = await api.get<{
        items: Audit[];
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
      }>(`/audits?status=${statusFilter}&pageSize=50`);
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_progress':
        return (
          <Badge variant="warning">
            <Clock className="h-3 w-3 mr-1" />
            In Progress
          </Badge>
        );
      case 'completed':
        return (
          <Badge variant="success">
            <CheckCircle className="h-3 w-3 mr-1" />
            Completed
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant="neutral">
            <XCircle className="h-3 w-3 mr-1" />
            Cancelled
          </Badge>
        );
      default:
        return <Badge variant="neutral">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Inventory Audits</h1>
          <p className="text-foreground-muted">Verify inventory counts by location</p>
        </div>
        <Button onClick={() => setShowStartModal(true)}>
          <Plus className="h-4 w-4" />
          Start Audit
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {(['all', 'in_progress', 'completed', 'cancelled'] as const).map((status) => (
          <Button
            key={status}
            variant={statusFilter === status ? 'primary' : 'ghost'}
            onClick={() => setStatusFilter(status)}
          >
            {status === 'all' ? 'All' : status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
          </Button>
        ))}
      </div>

      <Card>
        <CardBody>
          {isLoading ? (
            <div className="text-center py-12">
              <div className="spinner h-8 w-8 mx-auto" />
            </div>
          ) : audits?.items?.length === 0 ? (
            <div className="text-center py-12 text-foreground-muted">
              <ClipboardCheck className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No audits found</p>
              <p className="text-sm">Start an audit to verify your inventory</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Toolroom</th>
                    <th>Started By</th>
                    <th>Started</th>
                    <th>Scans</th>
                    <th>Discrepancies</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {audits?.items?.map((audit) => (
                    <tr key={audit.id}>
                      <td className="font-medium">{audit.toolroom_name}</td>
                      <td>
                        <span className="flex items-center gap-2 text-foreground-muted">
                          <User className="h-4 w-4" />
                          {audit.started_by_name}
                        </span>
                      </td>
                      <td className="text-foreground-muted text-sm">
                        {new Date(audit.started_at).toLocaleString()}
                      </td>
                      <td>{audit.scan_count || 0}</td>
                      <td>
                        {(audit.discrepancy_count || 0) > 0 ? (
                          <span className="flex items-center gap-1 text-error">
                            <AlertTriangle className="h-4 w-4" />
                            {audit.discrepancy_count}
                          </span>
                        ) : (
                          <span className="text-success">0</span>
                        )}
                      </td>
                      <td>{getStatusBadge(audit.status)}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setViewingAudit(audit.id)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {audit.status === 'in_progress' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setScanningAudit(audit.id)}
                              title="Continue Scanning"
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Start Audit Modal */}
      {showStartModal && (
        <StartAuditModal
          onClose={() => setShowStartModal(false)}
          onSuccess={(auditId) => {
            setShowStartModal(false);
            queryClient.invalidateQueries({ queryKey: ['audits'] });
            setScanningAudit(auditId);
          }}
        />
      )}

      {/* View Audit Details Modal */}
      {viewingAudit && (
        <AuditDetailsModal
          auditId={viewingAudit}
          onClose={() => setViewingAudit(null)}
        />
      )}

      {/* Scanning Modal */}
      {scanningAudit && (
        <ScanningModal
          auditId={scanningAudit}
          onClose={() => {
            setScanningAudit(null);
            queryClient.invalidateQueries({ queryKey: ['audits'] });
          }}
        />
      )}
    </div>
  );
}

// Start Audit Modal
interface StartAuditModalProps {
  onClose: () => void;
  onSuccess: (auditId: string) => void;
}

function StartAuditModal({ onClose, onSuccess }: StartAuditModalProps) {
  const [formData, setFormData] = useState({ toolroomId: '', notes: '' });
  const [error, setError] = useState('');

  const { data: toolrooms } = useQuery({
    queryKey: ['toolrooms'],
    queryFn: async () => {
      const res = await api.get<Toolroom[]>('/locations/toolrooms');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await api.post<{ id: string }>('/audits', {
        toolroomId: data.toolroomId,
        notes: data.notes || undefined,
      });
      if (!res.success) throw new Error(res.error || 'Failed to start audit');
      return res.data;
    },
    onSuccess: (data) => {
      if (data?.id) {
        onSuccess(data.id);
      }
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to start audit');
    },
  });

  const handleSubmit = () => {
    if (!formData.toolroomId) {
      setError('Please select a toolroom');
      return;
    }
    mutation.mutate(formData);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Start Inventory Audit"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>
            <Play className="h-4 w-4" />
            Start Audit
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>}

        <Select
          label="Toolroom *"
          options={
            toolrooms?.map((tr) => ({
              value: tr.id,
              label: tr.name,
            })) || []
          }
          value={formData.toolroomId}
          onChange={(e) => setFormData({ ...formData, toolroomId: e.target.value })}
        />

        <div>
          <label className="block text-sm font-medium mb-1">Notes</label>
          <textarea
            className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm resize-none"
            rows={3}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Optional notes about this audit..."
          />
        </div>
      </div>
    </Modal>
  );
}

// Audit Details Modal
interface AuditDetailsModalProps {
  auditId: string;
  onClose: () => void;
}

function AuditDetailsModal({ auditId, onClose }: AuditDetailsModalProps) {
  const { data: audit, isLoading } = useQuery({
    queryKey: ['audit', auditId],
    queryFn: async () => {
      const res = await api.get<AuditDetails>(`/audits/${auditId}`);
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Audit Details"
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {isLoading ? (
        <div className="text-center py-12">
          <div className="spinner h-8 w-8 mx-auto" />
        </div>
      ) : audit ? (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-surface-hover rounded-lg">
            <div className="text-center">
              <p className="text-2xl font-bold">{audit.summary?.total_scans || 0}</p>
              <p className="text-sm text-foreground-muted">Total Scans</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-success">{audit.summary?.match_count || 0}</p>
              <p className="text-sm text-foreground-muted">Matched</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-warning">{audit.summary?.over_count || 0}</p>
              <p className="text-sm text-foreground-muted">Over</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-error">{audit.summary?.under_count || 0}</p>
              <p className="text-sm text-foreground-muted">Under</p>
            </div>
          </div>

          {/* Scans */}
          <div>
            <h3 className="font-medium mb-3">Scan Results</h3>
            {audit.scans?.length === 0 ? (
              <p className="text-foreground-muted text-center py-4">No scans recorded</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {audit.scans?.map((scan) => (
                  <div
                    key={scan.id}
                    className={`p-3 rounded-lg border ${
                      scan.discrepancy === 0
                        ? 'border-success/30 bg-success/5'
                        : scan.discrepancy > 0
                        ? 'border-warning/30 bg-warning/5'
                        : 'border-error/30 bg-error/5'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-foreground-muted" />
                        <span className="font-medium">{scan.location_name}</span>
                        {scan.location_barcode && (
                          <span className="text-xs text-foreground-muted font-mono">
                            {scan.location_barcode}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-sm">
                          {scan.actual_quantity} / {scan.expected_quantity}
                        </span>
                        {scan.discrepancy !== 0 && (
                          <span
                            className={`ml-2 font-bold ${
                              scan.discrepancy > 0 ? 'text-warning' : 'text-error'
                            }`}
                          >
                            ({scan.discrepancy > 0 ? '+' : ''}{scan.discrepancy})
                          </span>
                        )}
                      </div>
                    </div>
                    {scan.notes && (
                      <p className="text-sm text-foreground-muted mt-1">{scan.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="text-center text-foreground-muted py-8">Audit not found</p>
      )}
    </Modal>
  );
}

// Scanning Modal
interface ScanningModalProps {
  auditId: string;
  onClose: () => void;
}

function ScanningModal({ auditId, onClose }: ScanningModalProps) {
  const queryClient = useQueryClient();
  const [selectedLocation, setSelectedLocation] = useState('');
  const [actualQuantity, setActualQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [lastScan, setLastScan] = useState<{ expected: number; actual: number; discrepancy: number } | null>(null);

  const { data: pendingLocations, isLoading } = useQuery({
    queryKey: ['audit-pending', auditId],
    queryFn: async () => {
      const res = await api.get<PendingLocation[]>(`/audits/${auditId}/pending-locations`);
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const scanMutation = useMutation({
    mutationFn: async (data: { locationId: string; actualQuantity: number; notes?: string }) => {
      const res = await api.post<{ expectedQuantity: number; actualQuantity: number; discrepancy: number }>(
        `/audits/${auditId}/scans`,
        data
      );
      if (!res.success) throw new Error(res.error || 'Failed to record scan');
      return res.data;
    },
    onSuccess: (data) => {
      setLastScan({
        expected: data?.expectedQuantity || 0,
        actual: data?.actualQuantity || 0,
        discrepancy: data?.discrepancy || 0,
      });
      setSelectedLocation('');
      setActualQuantity('');
      setNotes('');
      setError('');
      queryClient.invalidateQueries({ queryKey: ['audit-pending', auditId] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to record scan');
    },
  });

  const completeMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/audits/${auditId}/complete`, {});
      if (!res.success) throw new Error(res.error || 'Failed to complete audit');
      return res.data;
    },
    onSuccess: () => {
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to complete audit');
    },
  });

  const handleScan = () => {
    if (!selectedLocation) {
      setError('Please select a location');
      return;
    }
    if (actualQuantity === '') {
      setError('Please enter the actual quantity');
      return;
    }
    scanMutation.mutate({
      locationId: selectedLocation,
      actualQuantity: parseInt(actualQuantity),
      notes: notes || undefined,
    });
  };

  const selectedLocationData = pendingLocations?.find((l) => l.id === selectedLocation);

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Audit Scanning"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => completeMutation.mutate()}
            loading={completeMutation.isPending}
          >
            <CheckCircle className="h-4 w-4" />
            Complete Audit
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>}

        {/* Last Scan Result */}
        {lastScan && (
          <div
            className={`p-4 rounded-lg border ${
              lastScan.discrepancy === 0
                ? 'border-success/30 bg-success/10'
                : lastScan.discrepancy > 0
                ? 'border-warning/30 bg-warning/10'
                : 'border-error/30 bg-error/10'
            }`}
          >
            <div className="flex items-center gap-2">
              {lastScan.discrepancy === 0 ? (
                <CheckCircle className="h-5 w-5 text-success" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-warning" />
              )}
              <span className="font-medium">
                Last Scan: {lastScan.actual} / {lastScan.expected}
                {lastScan.discrepancy !== 0 && (
                  <span className="ml-2">
                    ({lastScan.discrepancy > 0 ? '+' : ''}{lastScan.discrepancy})
                  </span>
                )}
              </span>
            </div>
          </div>
        )}

        {/* Remaining count */}
        <div className="p-3 bg-surface-hover rounded-lg">
          <p className="text-sm text-foreground-muted">
            <strong>{pendingLocations?.length || 0}</strong> locations remaining
          </p>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <div className="spinner h-8 w-8 mx-auto" />
          </div>
        ) : pendingLocations?.length === 0 ? (
          <div className="text-center py-8 text-foreground-muted">
            <CheckCircle className="h-12 w-12 mx-auto mb-2 text-success" />
            <p className="font-medium">All locations scanned!</p>
            <p className="text-sm">Click "Complete Audit" to finish.</p>
          </div>
        ) : (
          <>
            <Select
              label="Location"
              options={
                pendingLocations?.map((loc) => ({
                  value: loc.id,
                  label: `${loc.name}${loc.barcode ? ` (${loc.barcode})` : ''} - Expected: ${loc.expected_quantity}`,
                })) || []
              }
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            />

            {selectedLocationData && (
              <div className="p-3 bg-info/10 border border-info/30 rounded-lg">
                <p className="text-sm">
                  <strong>Expected quantity:</strong> {selectedLocationData.expected_quantity}
                </p>
              </div>
            )}

            <Input
              label="Actual Quantity *"
              type="number"
              min="0"
              value={actualQuantity}
              onChange={(e) => setActualQuantity(e.target.value)}
              placeholder="Enter counted quantity"
            />

            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm resize-none"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes..."
              />
            </div>

            <Button onClick={handleScan} loading={scanMutation.isPending} className="w-full">
              <Package className="h-4 w-4" />
              Record Scan
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}
